import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Linking } from 'react-native';
import { BLUE, CARD, TEXT, TEXT_SUB, TEXT_MUTED, LIVE_GREEN, DIVIDER } from '../theme/colors';

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const months = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];
  return `${d.getDate()}. ${months[d.getMonth()]}`;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m unna`;
  if (km < 10) return `${km.toFixed(1).replace('.', ',')} km unna`;
  return `${Math.round(km)} km unna`;
}

function openMaps(lat: number, lng: number) {
  const url = Platform.select({
    ios: `maps://app?daddr=${lat},${lng}`,
    android: `geo:${lat},${lng}?q=${lat},${lng}`,
  });
  if (url) Linking.openURL(url);
}

function MapsButton({ lat, lng }: { lat: number; lng: number }) {
  if (Platform.OS === 'web') {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    return (
      // @ts-ignore – native <a> element, valid on web
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#4682B4',
          fontSize: 13,
          fontWeight: '600',
          textDecoration: 'none',
          padding: '4px 10px',
          border: '1px solid #4682B4',
          borderRadius: 8,
        }}
      >
        Veibeskrivelse
      </a>
    );
  }
  return (
    <TouchableOpacity
      style={styles.mapButton}
      onPress={() => openMaps(lat, lng)}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      <Text style={styles.mapButtonText}>Veibeskrivelse</Text>
    </TouchableOpacity>
  );
}

type UserLocation = { latitude: number; longitude: number } | null;

export default function CompetitionCard({ competition, userLocation, tickets }: { competition: any; userLocation: UserLocation; tickets?: any[] }) {
  const isLive = competition.status === 'in_progress';
  const isDone = competition.status === 'completed';
  const isEntrance = competition.status === 'entrance';

  const hasCoords = competition.latitude != null && competition.longitude != null;
  const distanceKm = hasCoords && userLocation
    ? haversineKm(userLocation.latitude, userLocation.longitude, competition.latitude, competition.longitude)
    : null;

  return (
    <View style={[styles.card, isLive && styles.cardLive]}>
      <View style={styles.row}>
        <Text style={styles.name}>{competition.name}</Text>
        {isLive && (
          <View style={styles.liveBadge}>
            <Text style={styles.liveText}>LIVE</Text>
          </View>
        )}
        {isEntrance && (
          <View style={[styles.liveBadge, { backgroundColor: BLUE }]}>
            <Text style={styles.liveText}>ÅPNER</Text>
          </View>
        )}
      </View>
      <View style={styles.locationRow}>
        {competition.location ? (
          <Text style={[styles.location, isLive && { color: BLUE }]}>{competition.location}</Text>
        ) : null}
        {distanceKm !== null && (
          <Text style={styles.distance}>{formatDistance(distanceKm)}</Text>
        )}
      </View>
      <View style={styles.bottomRow}>
        <Text style={styles.date}>
          {formatDate(competition.date)}
          {isDone ? ' · resultater' : ''}
        </Text>
        {hasCoords && (
          <MapsButton lat={competition.latitude} lng={competition.longitude} />
        )}
      </View>
      {tickets && tickets.length > 0 ? (
        <View style={styles.ticketsRow}>
          <Text style={styles.ticketsLabel}>🎟 Inngang: </Text>
          <Text style={styles.ticketsItems}>
            {tickets.map(t => `${t.name} ${t.price != null ? t.price.toFixed(0) + ',-' : ''}`).join('  ·  ')}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: CARD,
    marginHorizontal: 12,
    marginVertical: 4,
    padding: 16,
    borderRadius: 10,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  cardLive: {
    borderLeftWidth: 3,
    borderLeftColor: LIVE_GREEN,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  name: { fontSize: 16, fontWeight: '600', color: TEXT, flex: 1, marginRight: 8 },
  liveBadge: {
    backgroundColor: LIVE_GREEN,
    borderRadius: 4,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  liveText: { color: '#fff', fontSize: 11, fontWeight: '700', letterSpacing: 0.5 },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 3,
    gap: 8,
  },
  location: { color: TEXT_SUB, fontSize: 14 },
  distance: { color: TEXT_MUTED, fontSize: 13 },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  date: { color: TEXT_MUTED, fontSize: 13 },
  mapButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: BLUE,
  },
  mapButtonText: { color: BLUE, fontSize: 12, fontWeight: '600' },
  ticketsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: DIVIDER,
    flexWrap: 'wrap',
  },
  ticketsLabel: { fontSize: 12, color: TEXT_MUTED, fontWeight: '600' },
  ticketsItems: { fontSize: 12, color: TEXT_SUB, flex: 1, flexWrap: 'wrap' },
});
